<script setup lang="ts">
import moment from "moment";
import { computed, ref, watch } from "vue";
import { TuButton, ButtonSeverity, isNullish } from "@tinyurldev/tu-ui-vue";
import { DatePicker } from "v-calendar";
import Popper from "vue-popperjs";

import { logError } from "@Resources/js/helpers/logger";
import { useNotify, useTranslation } from "@Resources/js/composables";
import TimePeriods, { Period } from "@Modules/analytics/Helpers/TimePeriods";

const notify = useNotify();
const { __ } = useTranslation();

//
// Props
interface Props {
  selectedPeriod: Period;
  maximumAvailableTime?: moment.Moment | null;
}

const props = defineProps<Props>();

//
// Emit
interface Events {
  (e: "period-selected", period: Period): void;
}

const emit = defineEmits<Events>();

//
// Data
type Picker = {
  start: Date | null;
  end: Date | null;
};

const popperRef = ref<{ showPopper: boolean }>();
const picker = ref<Picker>({
  start: null,
  end: null,
});

//
// Computed
const allTimePeriod = computed<Period | null>(() => {
  if (isNullish(props.maximumAvailableTime) || props.maximumAvailableTime.isBefore(moment().subtract(1, "year"), "day")) {
    return null;
  }

  const start = props.maximumAvailableTime.clone();
  const end = moment();

  return {
    value: "all_time",
    label: getCustomLabel(start, end),
    start: () => start,
    end: () => end,
  };
});

//
// Methods
function getCustomLabel(start: moment.Moment, end: moment.Moment): string {
  const availableFormats = {
    sameDay: "[Today] LT",
    nextDay: "[Tomorrow] LT",
    nextWeek: "dddd LT",
    lastDay: "[Yesterday] LT",
    lastWeek: "MM/DD/YYYY LT",
    sameElse: "MM/DD/YYYY LT",
  };

  return `${start.format("MM/DD/YYYY")} - ${end.calendar(null, availableFormats)}`;
}

function hidePopover(): void {
  if (isNullish(popperRef.value)) {
    logError(`[hidePopover]: popperRef is not properly set.`);

    return;
  }

  popperRef.value.showPopper = false;

}

function onPeriodSelected(period: Period): void {
  hidePopover();

  emit("period-selected", period);
}

function onCustomPeriodSelected(): void {
  if (isNullish(picker.value.start) || isNullish(picker.value.end)) {
    logError("[onCustomPeriodSelected]: picker dates are not properly set.");

    return;
  }

  const start = moment(picker.value.start).startOf("day");
  const end = moment(picker.value.end).endOf("day");
  const isOneYearHigher = end.isAfter(start.clone().add(1, "year"));

  if (isOneYearHigher) {
    logError("[onCustomPeriodSelected]: selected date range cannot exceed 1 year.");
    notify.notifyError("The selected date range cannot exceed 1 year. Please select a shorter period.");

    return;
  }

  hidePopover();

  const period: Period = {
    value: "custom",
    label: getCustomLabel(start, end),
    start: () => start,
    end: () => end,
  };

  emit("period-selected", period);
}

function onPickerChange(newPicker:Picker) {
  picker.value = newPicker;
}

watch(() => props.selectedPeriod, (period) => {
  picker.value = {
    start: period.start().toDate(),
    end: period.end().toDate(),
  };
}, { immediate: true });
</script>

<template>
  <Popper
    ref="popperRef"
    class="period-picker"
    trigger="clickToToggle"
    :options="{
      placement: 'bottom',
      modifiers: { offset: { offset: '0,10' } },
    }"
  >
    <div class="popper">
      <button
        v-for="period in TimePeriods"
        :key="period.value"
        class="_dropdown-item dropdown-item text-base"
        :class="[
          { active: period.value === selectedPeriod.value },
          `_dropdown-item-${period.value}`,
        ]"
        @click.stop="onPeriodSelected(period)"
      >
        {{ period.label }}
      </button>
      <button
        v-if="allTimePeriod"
        class="_dropdown-item _all_time dropdown-item text-base"
        :class="{ active: allTimePeriod.value === selectedPeriod.value }"
        @click.stop="onPeriodSelected(allTimePeriod)"
      >
        {{ __("all_time") }}
      </button>

      <div class="period-picker__date-picker-wrapper">
        <DatePicker
          :value="picker"
          class="period-picker__date-picker"
          color="blue"
          is-range
          trim-weeks
          :max-date="new Date()"
          @input="onPickerChange"
        />

        <TuButton
          class="_select-button-period"
          label="Select Period"
          @click="onCustomPeriodSelected"
        />
      </div>
    </div>

    <TuButton
      slot="reference"
      class="_button-picker"
      icon-class="fa-solid fa-calendar _period-button"
      is-outlined
      :label="selectedPeriod.label"
      :severity="ButtonSeverity.CANCEL"
    />
  </Popper>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;

.period-picker {
  &__date-picker-wrapper {
    display: flex;
    gap: spacing.$xxs;
    flex-flow: column;
    padding: spacing.$xxs;

    border-top: border.$primary;
  }
}
</style>
